import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Image as ImageIcon,
  Images,
  MapPin,
  Megaphone,
  RefreshCcw,
  Search,
  User,
  MessageCircle,
  X,
} from 'lucide-react';
import { saveAs } from 'file-saver';

import axiosClient from '../api/axiosClient';
import PhotoAlbum from '../components/PhotoAlbum';

import {
  formatDate,
  getAfterPhotos,
  getAllPhotos,
  getBeforePhotos,
  getCampaignName,
  getFacadePhotos,
  getImageExtension,
  getImageUrl,
  getUserName,
  sanitizeFileName,
} from '../utils/helpers';

import '../styles/branding.css';

const REGIONS = [
  'ALL',
  'Ariana',
  'Béja',
  'Ben Arous',
  'Bizerte',
  'Gabès',
  'Gafsa',
  'Jendouba',
  'Kairouan',
  'Kasserine',
  'Kébili',
  'Le Kef',
  'Mahdia',
  'La Manouba',
  'Médenine',
  'Monastir',
  'Nabeul',
  'Sfax',
  'Sidi Bouzid',
  'Siliana',
  'Sousse',
  'Tataouine',
  'Tozeur',
  'Tunis',
  'Zaghouan',
];

export default function ClientBrandingPage() {
  const [brandings, setBrandings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedRegion, setSelectedRegion] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const totalBrandings = pagination?.total || brandings.length;

  const getPhotoPath = (photo) => {
    if (!photo) return '';
    return typeof photo === 'string' ? photo : photo.path;
  };

  const getPhotoKey = (photo, branding, index = 0) => {
    return `${branding?.id || 'branding'}-${photo?.id || index}`;
  };

  const buildParams = (page = 1, overrides = {}) => {
    const params = new URLSearchParams();

    const regionValue = overrides.selectedRegion ?? selectedRegion;
    const dateFromValue = overrides.dateFrom ?? dateFrom;
    const dateToValue = overrides.dateTo ?? dateTo;
    const searchValue = overrides.search ?? search;

    params.append('page', String(page));

    if (regionValue !== 'ALL') {
      params.append('region', regionValue);
    }

    if (dateFromValue.trim()) {
      params.append('date_from', dateFromValue.trim());
    }

    if (dateToValue.trim()) {
      params.append('date_to', dateToValue.trim());
    }

    if (searchValue.trim()) {
      params.append('search', searchValue.trim());
    }

    return params.toString();
  };

  const fetchBrandings = async (page = 1, showMainLoader = true, overrides = {}) => {
    try {
      if (showMainLoader) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }

      setErrorMessage('');

      const response = await axiosClient.get(`/brandings?${buildParams(page, overrides)}`);

      const paginatedData = response.data?.data;
      const list = paginatedData?.data || [];

      setBrandings(list);
      setPagination(paginatedData);
      setCurrentPage(paginatedData?.current_page || page);
    } catch (error) {
      console.log('CLIENT WEB BRANDINGS ERROR:', error?.response?.data || error.message);

      setErrorMessage(
        error?.response?.data?.message || 'Impossible de charger les brandings.'
      );
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandings(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (previewPhoto) {
      document.body.classList.add('photo-modal-open');
    } else {
      document.body.classList.remove('photo-modal-open');
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setPreviewPhoto(null);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.classList.remove('photo-modal-open');
      window.removeEventListener('keydown', handleEscape);
    };
  }, [previewPhoto]);

  const applyFilters = () => {
    setCurrentPage(1);
    fetchBrandings(1, false);
  };

  const resetFilters = () => {
    setSelectedRegion('ALL');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setCurrentPage(1);

    fetchBrandings(1, false, {
      selectedRegion: 'ALL',
      dateFrom: '',
      dateTo: '',
      search: '',
    });
  };

  const refreshCurrentPage = () => {
    fetchBrandings(currentPage, false);
  };

  const goToPreviousPage = () => {
    if (!pagination || currentPage <= 1) return;
    fetchBrandings(currentPage - 1, false);
  };

  const goToNextPage = () => {
    if (!pagination || currentPage >= pagination.last_page) return;
    fetchBrandings(currentPage + 1, false);
  };

  const openPhotoPreview = ({ title, photo, branding, index }) => {
    setPreviewPhoto({
      title,
      photo,
      branding,
      index,
    });
  };

  const closePhotoPreview = () => {
    setPreviewPhoto(null);
  };

  const buildPhotoFileName = (photo, branding, index = 0) => {
    const path = getPhotoPath(photo);
    const url = getImageUrl(path);
    const ext = getImageExtension(url);

    const outletName = sanitizeFileName(branding?.outlet?.name || 'outlet');
    const campaignName = sanitizeFileName(branding?.nomCampagne || 'campagne');
    const typeLabel = sanitizeFileName(photo?.typeLabel || photo?.type || 'photo');

    return `branding_${branding?.id || 'item'}_${campaignName}_${outletName}_${typeLabel}_${index + 1}.${ext}`;
  };

  const handleDownloadSinglePhoto = async (photo, branding, index = 0) => {
    const path = getPhotoPath(photo);

    if (!path) {
      alert('Chemin de photo introuvable.');
      return;
    }

    const key = getPhotoKey(photo, branding, index);

    try {
      setDownloadingKey(key);

      const response = await axiosClient.get('/brandings/download-photo', {
        params: { path },
        responseType: 'blob',
      });

      const fileName = buildPhotoFileName(photo, branding, index);

      saveAs(response.data, fileName);
    } catch (error) {
      console.log('DOWNLOAD SINGLE PHOTO ERROR:', error?.response || error);

      alert(
        "Impossible de télécharger cette photo. Vérifie que la route /brandings/download-photo existe bien dans Laravel et qu'elle est avant /brandings/{id}/download-photos."
      );
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleDownloadAllPhotos = async (branding) => {
    try {
      setDownloadingKey(`all-${branding.id}`);

      const allPhotos = getAllPhotos(branding);

      if (allPhotos.length === 0) {
        alert('Ce branding ne contient aucune photo.');
        return;
      }

      const campaignName = sanitizeFileName(branding.nomCampagne || 'campagne');
      const outletName = sanitizeFileName(branding.outlet?.name || 'outlet');

      const response = await axiosClient.get(`/brandings/${branding.id}/download-photos`, {
        responseType: 'blob',
      });

      saveAs(response.data, `branding_${branding.id}_${campaignName}_${outletName}.zip`);
    } catch (error) {
      console.log('DOWNLOAD ALL PHOTOS ERROR:', error?.response?.data || error.message);
      alert('Impossible de télécharger toutes les photos.');
    } finally {
      setDownloadingKey(null);
    }
  };

  const hasActiveFilters = useMemo(() => {
    return selectedRegion !== 'ALL' || dateFrom || dateTo || search;
  }, [selectedRegion, dateFrom, dateTo, search]);

  const renderPagination = () => {
    if (!pagination || pagination.last_page <= 1) return null;

    const from = pagination.from || 0;
    const to = pagination.to || 0;
    const total = pagination.total || 0;
    const lastPage = pagination.last_page || 1;

    return (
      <div className="pagination-card">
        <div className="pagination-info">
          {from} - {to} sur {total}
        </div>

        <div className="pagination-actions">
          <button
            type="button"
            className="page-btn"
            onClick={goToPreviousPage}
            disabled={currentPage <= 1 || pageLoading}
          >
            <ChevronLeft size={20} />
          </button>

          <div className="page-number">
            {pageLoading ? <span className="small-loader" /> : `${currentPage} / ${lastPage}`}
          </div>

          <button
            type="button"
            className="page-btn"
            onClick={goToNextPage}
            disabled={currentPage >= lastPage || pageLoading}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="branding-loading">
        <span className="large-loader" />
        <p>Chargement des brandings...</p>
      </div>
    );
  }

  return (
    <div className="branding-page">
      <section className="branding-top-card">
        <div>
          <p className="page-kicker">Espace Client</p>
          <h1>Consulter branding</h1>
          <p>
            Consultation des brandings validés avec photos façade, avant et après.
          </p>
        </div>

        <div className="branding-total-box">
          <span>{totalBrandings}</span>
          <small>Brandings</small>
        </div>
      </section>

      <section className="filter-card">
        <div className="filter-header">
          <div>
            <h2>Filtres</h2>
            <p>Affinez les résultats par région, campagne ou date de visite.</p>
          </div>

          <button
            type="button"
            className="refresh-btn"
            onClick={refreshCurrentPage}
            disabled={pageLoading}
          >
            <RefreshCcw size={18} />
            Actualiser
          </button>
        </div>

        <div className="filters-grid">
          <label className="filter-field">
            <span>Région</span>
            <div className="filter-input-box">
              <MapPin size={18} />
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
              >
                {REGIONS.map((region) => (
                  <option value={region} key={region}>
                    {region === 'ALL' ? 'Toutes les régions' : region}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className="filter-field">
            <span>Campagne</span>
            <div className="filter-input-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Rechercher par campagne"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
              />
            </div>
          </label>

          <label className="filter-field">
            <span>Date début</span>
            <div className="filter-input-box">
              <Calendar size={18} />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
          </label>

          <label className="filter-field">
            <span>Date fin</span>
            <div className="filter-input-box">
              <Calendar size={18} />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </label>
        </div>

        <div className="filter-actions">
          <button
            type="button"
            className="apply-filter-btn"
            onClick={applyFilters}
            disabled={pageLoading}
          >
            <Search size={18} />
            Appliquer
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              className="reset-filter-btn"
              onClick={resetFilters}
              disabled={pageLoading}
            >
              <RefreshCcw size={18} />
              Reset
            </button>
          )}
        </div>
      </section>

      {errorMessage && <div className="branding-error">{errorMessage}</div>}

      {brandings.length === 0 ? (
        <section className="empty-brandings">
          <Images size={54} />
          <h3>Aucun branding trouvé</h3>
          <p>Aucun résultat ne correspond aux filtres sélectionnés.</p>
        </section>
      ) : (
        <section className="branding-list">
          {brandings.map((item) => {
            const facadePhotos = getFacadePhotos(item);
            const beforePhotos = getBeforePhotos(item);
            const afterPhotos = getAfterPhotos(item);

            const totalPhotos =
              facadePhotos.length + beforePhotos.length + afterPhotos.length;

            const isDownloadingAll = downloadingKey === `all-${item.id}`;

            return (
              <article className="branding-card" key={item.id}>
                <div className="branding-card-header">
                  <div className="branding-icon-box">
                    <Images size={27} />
                  </div>

                  <div className="branding-main-info">
                    <h2>{item.outlet?.name || 'Outlet non défini'}</h2>

                    <div className="branding-campaign">
                      <Megaphone size={15} />
                      <span>{getCampaignName(item)}</span>
                    </div>

                    <div className="branding-meta">
                      <span>
                        <MapPin size={14} />
                        {item.outlet?.region || 'Région non définie'}
                      </span>

                      <span>
                        <Calendar size={14} />
                        Date visite : {formatDate(item.date_visite || item.created_at)}
                      </span>

                      <span>
                        <User size={14} />
                        Par {getUserName(item)}
                      </span>
                    </div>
                  </div>

                  <div className="approved-badge">
                    <CheckCircle size={15} />
                    Approuvé
                  </div>
                </div>

                <div className="albums-grid">
                  <PhotoAlbum
                    title="Photos façade"
                    photos={facadePhotos}
                    icon={Building2}
                    branding={item}
                    onDownloadPhoto={handleDownloadSinglePhoto}
                    onPreviewPhoto={openPhotoPreview}
                    downloadingKey={downloadingKey}
                  />

                  <PhotoAlbum
                    title="Photos avant"
                    photos={beforePhotos}
                    icon={ImageIcon}
                    branding={item}
                    onDownloadPhoto={handleDownloadSinglePhoto}
                    onPreviewPhoto={openPhotoPreview}
                    downloadingKey={downloadingKey}
                  />

                  <PhotoAlbum
                    title="Photos après"
                    photos={afterPhotos}
                    icon={Images}
                    branding={item}
                    onDownloadPhoto={handleDownloadSinglePhoto}
                    onPreviewPhoto={openPhotoPreview}
                    downloadingKey={downloadingKey}
                  />
                </div>

                <div className="branding-remark">
                  <MessageCircle size={17} />
                  <span>{item.remarque || 'Aucune remarque'}</span>
                </div>

                <button
                  type="button"
                  className="download-all-btn"
                  onClick={() => handleDownloadAllPhotos(item)}
                  disabled={isDownloadingAll || totalPhotos === 0}
                >
                  {isDownloadingAll ? (
                    <span className="button-loader" />
                  ) : (
                    <>
                      <Download size={19} />
                      Télécharger toutes les photos ({totalPhotos})
                    </>
                  )}
                </button>
              </article>
            );
          })}
        </section>
      )}

      {renderPagination()}

      {previewPhoto &&
        createPortal(
          <div className="photo-modal-overlay" onClick={closePhotoPreview}>
            <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
              <div className="photo-modal-header">
                <div>
                  <span className="photo-modal-kicker">Aperçu photo</span>
                  <h3>{previewPhoto.title}</h3>
                  <p>
                    {previewPhoto.branding?.outlet?.name || 'Outlet non défini'} — Photo{' '}
                    {previewPhoto.index + 1}
                  </p>
                </div>

                <button
                  type="button"
                  className="photo-modal-close"
                  onClick={closePhotoPreview}
                  aria-label="Fermer"
                >
                  <X size={23} />
                </button>
              </div>

              <div className="photo-modal-body">
                <img
                  src={getImageUrl(getPhotoPath(previewPhoto.photo))}
                  alt={previewPhoto.title || 'Photo branding'}
                />
              </div>

              <div className="photo-modal-footer">
                <button
                  type="button"
                  className="photo-modal-download"
                  onClick={() =>
                    handleDownloadSinglePhoto(
                      previewPhoto.photo,
                      previewPhoto.branding,
                      previewPhoto.index
                    )
                  }
                  disabled={
                    downloadingKey ===
                    getPhotoKey(previewPhoto.photo, previewPhoto.branding, previewPhoto.index)
                  }
                >
                  {downloadingKey ===
                  getPhotoKey(previewPhoto.photo, previewPhoto.branding, previewPhoto.index) ? (
                    <span className="button-loader light" />
                  ) : (
                    <>
                      <Download size={18} />
                      Télécharger cette photo
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="photo-modal-secondary"
                  onClick={closePhotoPreview}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}